import { connect, useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';

import { UserPermissions } from 'common/features/userPermissions/PermissionsEnum';
import { hasOnlyPermission, transformPermissions } from 'common/features/userPermissions/util';
import { useCallback, useMemo } from 'react';
import { getUser, userHasAuthenticated } from 'redux/slices/authSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import { useAuthLicenseAddOns } from '../features/licenseFeatures/useAuthLicenseAddOns';
import { useDefaultServiceRoute } from '../hooks/useDefaultServiceRoute';

const ProtectedRoute = ({
    roles,
    user,
    isAuthenticated,
    hiddenIfProfile,
    permissions,
    authorizations,
    main,
    strictPermissions,
}) => {
    const isAuthorize = useAuthLicenseAddOns();
    const organization = useSelector(getOrganization);
    const defaultRedirect = useDefaultServiceRoute();
    const userPermissions = useMemo(() => transformPermissions(user?.permissions), [user?.permissions]);
    const redirectPath = useCallback(() => {
        return isAuthenticated ? defaultRedirect : '/login';
    }, [defaultRedirect, isAuthenticated]);

    const isRoot = user?.role === 'root';
    const isOrgAdmin = !user?.userGroupId;
    const hasEmptyPermissions = Array.isArray(user?.permissions) && user.permissions.length === 0;

    // For strict resources (e.g., ievents), only root can bypass
    // For non-strict resources, orgAdmin and empty permissions can also bypass
    const shouldBypassPermissions = strictPermissions ? isRoot : isRoot || (isOrgAdmin && hasEmptyPermissions);

    // Permission check logic:
    // - Strict resources: require actions.length > 0
    // - Non-strict resources: presence of permission object is enough (even with empty actions)
    const hasRequiredPermissions = () => {
        if (!permissions || permissions.length === 0) return true;
        if (!userPermissions) return true;

        return permissions.every((perm) => {
            return userPermissions[perm]?.actions?.length > 0;
        });
    };

    // A user scoped exclusively to qrConfirmation can only access routes that require it
    const onlyHasQrConfirmation = hasOnlyPermission(user, UserPermissions.QR_CONFIRMATION);
    const allowedForQrOnlyUser = !onlyHasQrConfirmation || permissions?.includes(UserPermissions.QR_CONFIRMATION);

    return isAuthenticated &&
        allowedForQrOnlyUser &&
        (!roles || isRoot || roles.findIndex((role) => role === user?.role) !== -1) &&
        (!hiddenIfProfile || !organization?.isProfile) &&
        (shouldBypassPermissions || hasRequiredPermissions()) &&
        (!authorizations || isAuthorize(authorizations, isRoot ? false : main, true)) ? (
        <Outlet />
    ) : (
        <Navigate replace={true} to={redirectPath()} />
    );
};

const mapStateToProps = (state) => ({
    isAuthenticated: userHasAuthenticated(state),
    user: getUser(state),
});

export default connect(mapStateToProps)(ProtectedRoute);
